/**
 * TicketCard - Base primitive for ticket-style cards with punch-hole divots
 * 
 * Creates a ticket/coupon-shaped card with true transparent cutouts on both sides.
 * Uses CSS masks with radial gradients to create the ticket shape, ensuring the
 * border is properly clipped and the holes are genuinely transparent.
 * 
 * @module butterUI/primitives/TicketCard
 * 
 * @example
 * ```tsx
 * // Default purple border (brand-200) and 14px punch holes
 * <TicketCard>
 *   <div className="p-6">Header content</div>
 *   <TicketDivider punch />
 *   <div className="p-6">Footer content</div>
 * </TicketCard>
 * 
 * // Custom border color and punch size via props
 * <TicketCard borderColor="#D1D5DB" punchRadius={16}>
 *   ...
 * </TicketCard>
 * 
 * // Custom values via Tailwind arbitrary values
 * <TicketCard className="[--ticket-border-color:#D1D5DB] [--ticket-punch-radius:16px]">
 *   ...
 * </TicketCard>
 * ```
 */

import {
  useCallback,
  useRef,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { cn } from '#utils';
import { TicketCardContext } from './context';

// Import the CSS styles
import './TicketCard.css';

// ============================================================================
// TicketCard Component
// ============================================================================

export interface TicketCardProps {
  /** Card content - should include TicketDivider for the punch effect */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /**
   * Border color for the card and punch hole arcs.
   * Can be any valid CSS color value.
   * Alternatively, use the CSS variable `--ticket-border-color` via className.
   * @default "#E9D7FE" (brand-200)
   */
  borderColor?: string;
  /**
   * Border width for the card outline and punch-hole arcs.
   * Can be a number (treated as pixels) or string with units (e.g., "1.25px").
   * Alternatively, use the CSS variable `--ticket-border-width` via className.
   * @default 2 (2px)
   */
  borderWidth?: number | string;
  /**
   * Outer corner radius of the card.
   * Can be a number (treated as pixels) or string with units (e.g., "28px", "1.75rem").
   * Alternatively, use the CSS variable `--ticket-corner-radius` via className.
   * @default 24 (24px, equivalent to rounded-3xl)
   */
  cornerRadius?: number | string;
  /**
   * Color for the dashed divider line. When omitted, falls back to `borderColor`.
   * Allows decorative dividers (e.g. theme-400 purple) on cards with neutral borders.
   * Alternatively, use the CSS variable `--ticket-divider-color` via className.
   * @default inherits from --ticket-border-color
   */
  dividerColor?: string;
  /**
   * Radius of the punch holes in pixels.
   * Can be a number (treated as pixels) or string with units (e.g., "14px", "1rem").
   * Alternatively, use the CSS variable `--ticket-punch-radius` via className.
   * @default 14 (14px)
   */
  punchRadius?: number | string;
  /**
   * Draw the punch-hole notches. Set false for a ticket with no divider, so no
   * bites sit on the edges.
   * @default true
   */
  punched?: boolean;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * TicketCard Component
 * 
 * A card with true transparent punch-hole cutouts on the sides, creating a 
 * ticket/coupon effect. The ticket shape is created using CSS masks with the 
 * divots positioned automatically based on the TicketDivider component.
 */
export function TicketCard({
  children,
  className,
  borderColor,
  borderWidth,
  cornerRadius,
  dividerColor,
  punchRadius,
  punched = true,
  'data-testid': testId,
}: TicketCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Callback for TicketDivider to register its position
  const registerPunchPosition = useCallback((element: HTMLElement | null) => {
    if (!element || !cardRef.current) return;

    const calculatePosition = () => {
      if (!element || !cardRef.current) return;
      
      const cardRect = cardRef.current.getBoundingClientRect();
      const dividerRect = element.getBoundingClientRect();
      
      // Position is the center of the divider relative to the card
      const relativeTop = dividerRect.top - cardRect.top + (dividerRect.height / 2);
      
      // Update CSS custom property for punch position
      cardRef.current.style.setProperty('--ticket-punch-y', `${relativeTop}px`);
    };

    calculatePosition();

    const resizeObserver = new ResizeObserver(calculatePosition);
    resizeObserver.observe(cardRef.current);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  // Build style object with CSS variables
  const wrapperStyle: CSSProperties = {
    '--ticket-punch-y': '50%', // Default fallback position
    ...(borderColor && { '--ticket-border-color': borderColor }),
    ...(borderWidth && {
      '--ticket-border-width': typeof borderWidth === 'number' ? `${borderWidth}px` : borderWidth,
    }),
    ...(cornerRadius && {
      '--ticket-corner-radius': typeof cornerRadius === 'number' ? `${cornerRadius}px` : cornerRadius,
    }),
    ...(dividerColor && { '--ticket-divider-color': dividerColor }),
    ...(punchRadius && {
      '--ticket-punch-radius': typeof punchRadius === 'number' ? `${punchRadius}px` : punchRadius,
    }),
  } as CSSProperties;

  return (
    <TicketCardContext.Provider value={{ registerPunchPosition, cardRef }}>
      <div
        ref={cardRef}
        className="relative"
        style={wrapperStyle}
        data-punched={punched}
        data-testid='ticket-card-wrapper'
      >
        {/* Inner semicircular border arcs - outside the masked container */}
        <div className="ticket-card-arcs" aria-hidden="true" />
        <div
          className={cn('ticket-card', className)}
          data-testid={testId}
        >
          {children}
        </div>
      </div>
    </TicketCardContext.Provider>
  );
}
